import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar
} from '@mui/material';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import StarRateIcon from '@mui/icons-material/StarRate';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { satisfactionToNumeric } from '../common/constants';

// Helper function to get color based on score
const getSatisfactionColor = (score) => {
  if (score >= 4.5) return '#22c55e'; // Green (Excellent)
  if (score >= 4.0) return '#84cc16'; // Lime (Very Good)
  if (score >= 3.5) return '#3b82f6'; // Blue (Good)
  if (score >= 3.0) return '#f59e0b'; // Amber (Satisfactory)
  if (score >= 2.5) return '#f97316'; // Orange (Fair)
  return '#ef4444'; // Red (Poor)
};

// SQD dimension mapping for better display names
const sqdDimensions = {
  sqd0: 'SQD0 - Reliability',
  sqd1: 'SQD1 - Responsiveness',
  sqd2: 'SQD2 - Access & Facilities',
  sqd3: 'SQD3 - Communication',
  sqd4: 'SQD4 - Costs',
  sqd5: 'SQD5 - Integrity',
  sqd6: 'SQD6 - Assurance',
  sqd7: 'SQD7 - Empathy',
  sqd8: 'SQD8 - Tangibles',
  sqd9: 'SQD9 - Overall Satisfaction',
  sqd10: 'SQD10 - Overall Satisfaction'
};

const SQDComprehensiveAnalysis = ({ sqdChartData, sqdScores, dashboardStats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [analysisResults, setAnalysisResults] = useState({
    strengths: [],
    weaknesses: [],
    trends: [],
    correlations: [],
    overallScore: 0,
    dimensionScores: [],
    avgSQD0to8: 0,
    avgSQD0to10: 0
  });

  useEffect(() => {
    if (sqdChartData && sqdChartData.length > 0) {
      analyzeSQDData();
    }
  }, [sqdChartData]);

  const analyzeSQDData = () => {
    // Calculate overall score
    const overallScore = sqdChartData.reduce((acc, curr) => acc + curr.score, 0) / sqdChartData.length;
    
    // Calculate average for SQD0-8
    const sqd0to8 = sqdChartData.filter(item => 
      item.id.startsWith('sqd') && 
      parseInt(item.id.replace('sqd', '')) >= 0 && 
      parseInt(item.id.replace('sqd', '')) <= 8
    );
    
    const avgSQD0to8 = sqd0to8.length > 0 
      ? sqd0to8.reduce((acc, curr) => acc + curr.score, 0) / sqd0to8.length 
      : 0;
    
    // Calculate average for SQD0-10
    const sqd0to10 = sqdChartData.filter(item => 
      item.id.startsWith('sqd') && 
      parseInt(item.id.replace('sqd', '')) >= 0 && 
      parseInt(item.id.replace('sqd', '')) <= 10
    );
    
    const avgSQD0to10 = sqd0to10.length > 0 
      ? sqd0to10.reduce((acc, curr) => acc + curr.score, 0) / sqd0to10.length 
      : 0;
    
    // Sort data for strengths and weaknesses analysis
    const sortedData = [...sqdChartData].sort((a, b) => b.score - a.score);
    
    // Identify strengths (top 3 dimensions)
    const strengths = sortedData.slice(0, 3).map(item => ({
      dimension: sqdDimensions[item.id] || item.name,
      id: item.id,
      score: item.score,
      percentAboveAverage: ((item.score / overallScore - 1) * 100).toFixed(2)
    }));
    
    // Identify weaknesses (bottom 3 dimensions)
    const weaknesses = sortedData.slice(-3).reverse().map(item => ({
      dimension: sqdDimensions[item.id] || item.name,
      id: item.id,
      score: item.score,
      percentBelowAverage: ((1 - item.score / overallScore) * 100).toFixed(2)
    }));
    
    // Create dimension detailed scores
    const dimensionScores = sortedData.map(item => ({
      id: item.id,
      dimension: sqdDimensions[item.id] || item.name,
      score: item.score,
      respondents: item.respondents || 0,
      percentageOfIdeal: (item.score / 5 * 100).toFixed(2),
      status: getStatusCategory(item.score)
    }));
    
    // Mockup trends data (in a real app, this would be calculated from historical data)
    // In a real implementation, you would use the historical data from sqdScores
    const mockTrends = sortedData.slice(0, 5).map(item => {
      const trend = Math.random() > 0.5 ? 'up' : (Math.random() > 0.5 ? 'down' : 'stable');
      const changePercent = ((Math.random() * 10) - 5).toFixed(2);
      return {
        dimension: sqdDimensions[item.id] || item.name,
        id: item.id,
        trend,
        changePercent,
        score: item.score,
        previousScore: item.score - (item.score * (parseFloat(changePercent) / 100))
      };
    });
    
    // Mockup correlations (in a real app, this would be calculated from actual data)
    const mockCorrelations = [
      { dimension1: 'Responsiveness', dimension2: 'Reliability', strength: 0.85, impact: 'high' },
      { dimension1: 'Assurance', dimension2: 'Empathy', strength: 0.72, impact: 'medium' },
      { dimension1: 'Tangibles', dimension2: 'Access', strength: 0.65, impact: 'medium' }
    ];
    
    setAnalysisResults({
      strengths,
      weaknesses,
      trends: mockTrends,
      correlations: mockCorrelations,
      overallScore,
      dimensionScores,
      avgSQD0to8,
      avgSQD0to10
    });
  };
  
  const getStatusCategory = (score) => {
    if (score >= 4.5) return 'Very Satisfied';
    if (score >= 3.5) return 'Satisfied';
    if (score >= 2.5) return 'Neither Unsatisfied nor Satisfied';
    if (score >= 1.5) return 'Unsatisfied';
    return 'Very Unsatisfied';
  };
  
  const getStatusChip = (status) => {
    const statusConfig = {
      'Very Satisfied': { color: 'success', icon: <CheckCircleOutlineIcon fontSize="small" />, label: 'Very Satisfied' },
      'Satisfied': { color: 'success', icon: null, label: 'Satisfied' },
      'Neither Unsatisfied nor Satisfied': { color: 'primary', icon: null, label: 'Neither Unsatisfied nor Satisfied' },
      'Unsatisfied': { color: 'warning', icon: <WarningAmberIcon fontSize="small" />, label: 'Unsatisfied' },
      'Very Unsatisfied': { color: 'error', icon: <ErrorOutlineIcon fontSize="small" />, label: 'Very Unsatisfied' }
    };
    
    const config = statusConfig[status] || statusConfig.satisfactory;
    
    return (
      <Chip 
        size="small" 
        color={config.color} 
        icon={config.icon} 
        label={config.label} 
        variant="outlined"
      />
    );
  };
  
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUpIcon color="success" />;
    if (trend === 'down') return <TrendingDownIcon color="error" />;
    return <TrendingFlatIcon color="action" />;
  };
  
  const radarData = sqdChartData.map(item => ({
    dimension: sqdDimensions[item.id] || item.name,
    score: item.score,
    fullMark: 5
  }));
  
  // Prepare data for distribution chart
  const distributionData = [
    { range: '0-1', count: 0 },
    { range: '1-2', count: 0 },
    { range: '2-3', count: 0 },
    { range: '3-4', count: 0 },
    { range: '4-5', count: 0 }
  ];
  
  sqdChartData.forEach(item => {
    const score = item.score;
    if (score < 1) distributionData[0].count++;
    else if (score < 2) distributionData[1].count++;
    else if (score < 3) distributionData[2].count++;
    else if (score < 4) distributionData[3].count++;
    else distributionData[4].count++;
  });

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Overall SQD Score
              </Typography>
              <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                {analysisResults.overallScore.toFixed(2)}
                <Typography variant="body2" sx={{ ml: 1 }}>/ 5.00</Typography>
              </Typography>
              <Chip 
                label={analysisResults.overallScore >= 4.5 ? 'Very Satisfied' : 
                      analysisResults.overallScore >= 3.5 ? 'Satisfied' : 
                      analysisResults.overallScore >= 2.5 ? 'Neither Unsatisfied nor Satisfied' : 
                      analysisResults.overallScore >= 1.5 ? 'Unsatisfied' : 'Very Unsatisfied'} 
                color={analysisResults.overallScore >= 4.5 ? 'success' : 
                      analysisResults.overallScore >= 3.5 ? 'success' : 
                      analysisResults.overallScore >= 2.5 ? 'primary' : 
                      analysisResults.overallScore >= 1.5 ? 'primary' : 'warning'} 
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Top Performing Dimension
              </Typography>
              {analysisResults.strengths.length > 0 && (
                <>
                  <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                    {analysisResults.strengths[0].score.toFixed(2)}
                    <StarRateIcon color="warning" sx={{ ml: 0.5 }} />
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {analysisResults.strengths[0].dimension}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Average SQD0-8
              </Typography>
              <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                {analysisResults.avgSQD0to8.toFixed(2)}
                <EqualizerIcon color="primary" sx={{ ml: 0.5 }} />
              </Typography>
              <Chip 
                label={analysisResults.avgSQD0to8 >= 4.5 ? 'Very Satisfied' : 
                      analysisResults.avgSQD0to8 >= 3.5 ? 'Satisfied' : 
                      analysisResults.avgSQD0to8 >= 2.5 ? 'Neither Unsatisfied nor Satisfied' : 
                      analysisResults.avgSQD0to8 >= 1.5 ? 'Unsatisfied' : 'Very Unsatisfied'} 
                color={analysisResults.avgSQD0to8 >= 4.5 ? 'success' : 
                      analysisResults.avgSQD0to8 >= 3.5 ? 'success' : 
                      analysisResults.avgSQD0to8 >= 2.5 ? 'primary' : 
                      analysisResults.avgSQD0to8 >= 1.5 ? 'primary' : 'warning'} 
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Average SQD0-10
              </Typography>
              <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                {analysisResults.avgSQD0to10.toFixed(2)}
                <EqualizerIcon color="primary" sx={{ ml: 0.5 }} />
              </Typography>
              <Chip 
                label={analysisResults.avgSQD0to10 >= 4.5 ? 'Very Satisfied' : 
                      analysisResults.avgSQD0to10 >= 3.5 ? 'Satisfied' : 
                      analysisResults.avgSQD0to10 >= 2.5 ? 'Neither Unsatisfied nor Satisfied' : 
                      analysisResults.avgSQD0to10 >= 1.5 ? 'Unsatisfied' : 'Very Unsatisfied'} 
                color={analysisResults.avgSQD0to10 >= 4.5 ? 'success' : 
                      analysisResults.avgSQD0to10 >= 3.5 ? 'success' : 
                      analysisResults.avgSQD0to10 >= 2.5 ? 'primary' : 
                      analysisResults.avgSQD0to10 >= 1.5 ? 'primary' : 'warning'} 
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Second Row of Cards - Showing More SQD Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Most Critical Dimension
              </Typography>
              {analysisResults.weaknesses.length > 0 && (
                <>
                  <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                    {analysisResults.weaknesses[0].score.toFixed(2)}
                    <ErrorOutlineIcon color="error" sx={{ ml: 0.5 }} />
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {analysisResults.weaknesses[0].dimension}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Gap (SQD0-8 vs SQD0-10)
              </Typography>
              <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                {(analysisResults.avgSQD0to8 - analysisResults.avgSQD0to10).toFixed(2)}
                {(analysisResults.avgSQD0to8 - analysisResults.avgSQD0to10) > 0 
                  ? <TrendingUpIcon color="success" sx={{ ml: 0.5 }} />
                  : <TrendingDownIcon color="error" sx={{ ml: 0.5 }} />
                }
              </Typography>
              <Typography variant="body2">
                {Math.abs((analysisResults.avgSQD0to8 / analysisResults.avgSQD0to10 - 1) * 100).toFixed(2)}% Difference
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                SQD0-8 vs Overall
              </Typography>
              <Typography variant="h4" sx={{ my: 1, display: 'flex', alignItems: 'center' }}>
                {(analysisResults.avgSQD0to8 - analysisResults.overallScore).toFixed(2)}
                {(analysisResults.avgSQD0to8 - analysisResults.overallScore) > 0 
                  ? <TrendingUpIcon color="success" sx={{ ml: 0.5 }} />
                  : <TrendingDownIcon color="error" sx={{ ml: 0.5 }} />
                }
              </Typography>
              <Typography variant="body2">
                {Math.abs((analysisResults.avgSQD0to8 / analysisResults.overallScore - 1) * 100).toFixed(2)}% Difference
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Dimensions Analyzed
              </Typography>
              <Typography variant="h4" sx={{ my: 1 }}>
                {sqdChartData.length}
              </Typography>
              <Typography variant="body2">
                Total Respondents: {dashboardStats || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Strengths and Weaknesses */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Key Strengths
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dimension</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">% Above Avg</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analysisResults.strengths.map((strength) => (
                    <TableRow key={strength.id}>
                      <TableCell>{strength.dimension}</TableCell>
                      <TableCell align="right">{strength.score.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        +{strength.percentAboveAverage}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Improvement Areas
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dimension</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">% Below Avg</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analysisResults.weaknesses.map((weakness) => (
                    <TableRow key={weakness.id}>
                      <TableCell>{weakness.dimension}</TableCell>
                      <TableCell align="right">{weakness.score.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        -{weakness.percentBelowAverage}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Charts Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Dimension Radar Analysis
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart outerRadius={90} data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 5]} />
                  <Radar name="SQD Score" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Score Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Number of Dimensions" fill="#82ca9d">
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getSatisfactionColor((index + 0.5))} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Comprehensive Table */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Detailed Dimension Analysis
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Dimension</TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell align="center">% of Ideal</TableCell>
                <TableCell align="center">Respondents</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Recommendation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analysisResults.dimensionScores.map((dimension) => (
                <TableRow key={dimension.id}>
                  <TableCell>{dimension.dimension}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {dimension.score.toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    {dimension.percentageOfIdeal}%
                  </TableCell>
                  <TableCell align="center">{dimension.respondents}</TableCell>
                  <TableCell align="center">
                    {getStatusChip(dimension.status)}
                  </TableCell>
                  <TableCell>
                    {dimension.status === 'Very Satisfied' && 'Maintain excellence, share best practices'}
                    {dimension.status === 'Satisfied' && 'Continue improvement efforts'}
                    {dimension.status === 'Neither Unsatisfied nor Satisfied' && 'Target for enhancement'}
                    {dimension.status === 'Unsatisfied' && 'Priority for improvement action plan'}
                    {dimension.status === 'Very Unsatisfied' && 'Critical - immediate intervention required'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Trends Section */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Dimensional Trends
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Performance changes compared to previous assessment period.
        </Typography>
        <Grid container spacing={2}>
          {analysisResults.trends.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2">{item.dimension}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {getTrendIcon(item.trend)}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        ml: 1,
                        color: item.trend === 'up' ? 'success.main' : (item.trend === 'down' ? 'error.main' : 'text.secondary')
                      }}
                    >
                      {item.trend === 'up' ? '+' : (item.trend === 'down' ? '-' : '')}
                      {Math.abs(parseFloat(item.changePercent))}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">Previous: {item.previousScore.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Current: {item.score.toFixed(2)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
      
      {/* Key Insights */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Key Insights & Recommendations
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Executive Summary</Typography>
            <Typography variant="body2" paragraph>
              The Service Quality Dimensions (SQD) analysis indicates an overall score of {analysisResults.overallScore.toFixed(2)} out of 5.00, 
              which places the service quality in the {analysisResults.overallScore >= 4.5 ? 'very satisfactory' : 
              analysisResults.overallScore >= 3.5 ? 'satisfactory' : analysisResults.overallScore >= 2.5 ? 'neither unsatisfactory nor satisfactory' : 
              (analysisResults.overallScore >= 1.5 ? 'unsatisfactory' : 'vary unsatisfactory')} category. 
              The SQD0-8 dimensions average {analysisResults.avgSQD0to8.toFixed(2)}, while SQD0-10 dimensions average {analysisResults.avgSQD0to10.toFixed(2)}.
              {analysisResults.strengths.length > 0 && ` The ${analysisResults.strengths[0].dimension} dimension shows the highest performance with a score of ${analysisResults.strengths[0].score.toFixed(2)}, `} 
              while {analysisResults.weaknesses.length > 0 && ` ${analysisResults.weaknesses[0].dimension} requires the most attention with a score of ${analysisResults.weaknesses[0].score.toFixed(2)}.`}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>Strategic Recommendations</Typography>
            <ul>
              {analysisResults.weaknesses.map((weakness) => (
                <li key={weakness.id}>
                  <Typography variant="body2">
                    <strong>Improve {weakness.dimension}:</strong> Develop targeted training and process improvements to address the  
                   {' '}{weakness.percentBelowAverage}% gap in this dimension.
                  </Typography>
                </li>
              ))}
              <li>
                <Typography variant="body2">
                  <strong>Enhance Client Communication:</strong> Implement regular updates to clients about service improvements based on survey feedback.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Service Consistency:</strong> Focus on standardizing service quality across all dimensions to reduce variability.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Address SQD0-8 vs SQD0-10 Gap:</strong> Analyze the {Math.abs((analysisResults.avgSQD0to8 / analysisResults.avgSQD0to10 - 1) * 100).toFixed(2)}% difference between core dimensions and overall satisfaction ratings.
                </Typography>
              </li>
            </ul>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>Actionable Steps</Typography>
            <ul>
              <li>
                <Typography variant="body2">
                  <strong>Short-term (30 days):</strong> Address critical dimensions scoring below 3.5 with immediate staff training.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Medium-term (90 days):</strong> Implement process improvements for dimensions scoring between 3.5-4.0.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Long-term (6 months):</strong> Develop a comprehensive quality management system to maintain high standards across all dimensions.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Continuous Monitoring:</strong> Establish monthly review cycles to track improvements in critical dimensions.
                </Typography>
              </li>
            </ul>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SQDComprehensiveAnalysis;